import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import { Button, Checkbox } from '../../../components/common';
import { DropdownOptionValue } from '../../../components/common/Dropdown';
import { FormActions, InputField, TextAreaElement, TimeDropdown } from '../../../components/common/forms';
import { scrollToTop } from '../../../utils';
import { FormPageContainer } from '../FormPageContainer';
import { ContentBlock, Form, FormRow, RequiredIndicator } from '../layoutComponents';
import { H2, H3 } from '../Text';
import { MeetingMinutesPreview } from './MeetingMinutesPreview';
import { MeetingMinutesFormState, schema } from './schema';
import { NoticeForm, NoticeFormType, NoticeRequestBody } from '../../../types/notices';
import { NotificationType, useAppDispatch } from '../../../context/appContext';
import { createNotice, publishNotice, updateNotice } from '../../../apis_kenno';
import { showNotification } from '../../../context/appActions';
import Submitted from '../Submitted';
import FormError from '../../../components/common/forms/FormError';

const MeetingDetailsContent = styled(ContentBlock)``;
const MeetingContent = styled(ContentBlock)``;
const SignatureContent = styled(ContentBlock)``;
const PublicationContent = styled(ContentBlock)``;

interface MeetingMinutesProps {
  updateForm?: NoticeForm;
}

export const MeetingMinutes: React.FC<MeetingMinutesProps> = ({ updateForm }) => {
  const appDispatch = useAppDispatch();
  const [isBusy, setIsBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialId = updateForm ? updateForm.id : null;
  const [formId, setFormId] = useState<string | null>(initialId);
  const [published, setPublished] = useState(false);

  const initialValues = {
    items: [{ title: '', text: '' }],
    signatoryConfirmation: false,
    participants: 0,
  };

  const formMethods = useForm<MeetingMinutesFormState>({
    resolver: zodResolver(schema),
    defaultValues: updateForm ? { ...initialValues, ...updateForm.formData } : { ...initialValues },
  });

  const [previewOpen, setPreviewOpen] = useState<boolean>(false);

  const t = useMessageGetter('forms.meetingMinutes');
  const tCommon = useMessageGetter('forms');

  const handleSubmitFinal = async (data: MeetingMinutesFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    const body: NoticeRequestBody = {
      formData: data,
      formType: NoticeFormType.MeetingMinutes,
    };

    try {
      let id = formId;
      if (id) {
        await updateNotice(id, body);
      } else {
        id = (await createNotice(body)).id;
      }
      await publishNotice(id);
      handleEmptyForm();
      setPublished(true);
      scrollToTop();
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.publish'));
    } finally {
      setIsBusy(false);
    }
  };

  const handleSave = async (data: MeetingMinutesFormState) => {
    setSubmitFailed(false);
    setErrorMessage('');
    setIsBusy(true);

    const body: NoticeRequestBody = {
      formData: data,
      formType: NoticeFormType.MeetingMinutes,
    };

    try {
      if (!formId) {
        const meetingMinutes = await createNotice(body);
        setFormId(meetingMinutes.id);
      } else {
        await updateNotice(formId, body);
      }
      await showNotification(appDispatch, tCommon('info.saved'), NotificationType.SUCCESS);
    } catch (e) {
      console.error(e);
      setSubmitFailed(true);
      setErrorMessage(tCommon('error.save'));
    } finally {
      setIsBusy(false);
    }
  };

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: 'items',
  });

  const handleAddNewHeading = () => {
    append({ title: '', text: '' });
  };

  const handleEmptyForm = () => {
    formMethods.reset();
  };

  const handleTogglePreview = () => {
    setPreviewOpen(!previewOpen);
    scrollToTop();
  };

  const translationPrefix = 'forms.meetingMinutes';

  return (
    <FormPageContainer<MeetingMinutesFormState> formMethods={formMethods}>
      {!previewOpen && <H2>{t('title')}</H2>}
      {published ? (
        <Submitted message={tCommon('info.published')} />
      ) : (
        <Form>
          {previewOpen ? (
            <MeetingMinutesPreview formData={formMethods.getValues()} />
          ) : (
            <>
              <MeetingDetailsContent>
                <span>
                  {t('description.part1')} (<RequiredIndicator>*</RequiredIndicator>) {t('description.part2')}
                </span>
                <div>
                  <FormRow>
                    <InputField<MeetingMinutesFormState, string>
                      fieldPath="name"
                      placeholder={t('name')}
                      translationPrefix={translationPrefix}
                      type="text"
                      required
                    />
                  </FormRow>
                  <FormRow>
                    <InputField<MeetingMinutesFormState, string>
                      fieldPath="date"
                      translationPrefix={translationPrefix}
                      type="date"
                      required
                    />
                    <InputField<MeetingMinutesFormState, DropdownOptionValue>
                      fieldPath="time"
                      translationPrefix={translationPrefix}
                      required
                      inputElementRenderer={(props) => <TimeDropdown {...props} width="230px" />}
                    />
                  </FormRow>
                  <FormRow>
                    <InputField<MeetingMinutesFormState, string>
                      fieldPath="location"
                      placeholder={t('locationPlaceholder')}
                      translationPrefix={translationPrefix}
                      type="text"
                      required
                    />
                  </FormRow>
                  <FormRow>
                    <InputField<MeetingMinutesFormState, number>
                      fieldPath="participants"
                      translationPrefix={translationPrefix}
                      type="number"
                      required
                      style={{ width: '25%' }}
                    />
                  </FormRow>
                </div>
              </MeetingDetailsContent>

              <MeetingContent>
                <H3>{t('content.title')}</H3>
                {t('content.description')}
                {fields.map((field, index) => (
                  <div key={field.id} style={{ marginBottom: '1rem' }}>
                    <InputField<MeetingMinutesFormState, string>
                      fieldPath={`items.${index}.title`}
                      translationPrefix={translationPrefix}
                      placeholder={t('items.title')}
                      type="text"
                      required
                    />

                    <InputField<MeetingMinutesFormState, string>
                      translationPrefix={translationPrefix}
                      fieldPath={`items.${index}.text`}
                      required
                      inputElementRenderer={(props) => (
                        <TextAreaElement {...props} placeholder={t('items.textPlaceholder')} />
                      )}
                    />
                    {fields.length > 1 && (
                      <Button primary onClick={() => remove(index)}>
                        {t('removeHeading')}
                      </Button>
                    )}
                  </div>
                ))}
                <FormRow>
                  <Button type="button" icon="plus" onClick={handleAddNewHeading}>
                    {t('addNewHeading')}
                  </Button>
                </FormRow>
              </MeetingContent>

              <SignatureContent>
                <H3>{t('signatures.title')}</H3>
                <div>
                  <InputField<MeetingMinutesFormState, string>
                    fieldPath="chairperson"
                    translationPrefix={translationPrefix}
                    placeholder={t('signaturePlaceholder')}
                    type="text"
                    required
                  />
                  <InputField<MeetingMinutesFormState, string>
                    fieldPath="secretary"
                    translationPrefix={translationPrefix}
                    placeholder={t('signaturePlaceholder')}
                    type="text"
                    required
                  />
                </div>
              </SignatureContent>

              <PublicationContent>
                <H3>{t('publication.title')}</H3>
                <div>
                  <InputField<MeetingMinutesFormState, DropdownOptionValue>
                    fieldPath="signatoryConfirmation"
                    translationPrefix={translationPrefix}
                    hideLabel
                    inputElementRenderer={({ value, id, onChange }) => (
                      <FormRow style={{ gap: '12px' }}>
                        {/* TODO: fix types.. */}
                        <Checkbox value={!!value} id={id} onChange={onChange} />
                        {t('signatoryConfirmation.description')}
                      </FormRow>
                    )}
                  />
                </div>
              </PublicationContent>
            </>
          )}
          {submitFailed && <FormError message={errorMessage} />}
          <FormActions
            handleEmptyForm={handleEmptyForm}
            handleTogglePreview={handleTogglePreview}
            handleSave={formMethods.handleSubmit(handleSave)}
            handleSubmitFinal={formMethods.handleSubmit(handleSubmitFinal)}
            previewOpen={previewOpen}
            disabled={isBusy}
          />
        </Form>
      )}
    </FormPageContainer>
  );
};
